import React, { useEffect, useMemo, useState } from "react"
import axios from "axios"
import { useDispatch } from "react-redux"
import BookCabMain from "./BookCabMain"
import Loading from "../../components/Loading"
import { cabAction } from "../../store/Cab/CAbReducer"
import { bookNow } from "../../store/Cab/CabAction"

const extractCity = (value = "") => {
  if (!value || typeof value !== "string") return ""
  return value.split(",")[0]?.trim() || value.trim()
}

const normalizeDate = (dateValue) => {
  if (!dateValue) return ""
  const date = new Date(dateValue)
  if (Number.isNaN(date.getTime())) return dateValue
  return date.toLocaleDateString("en-CA")
}

const normalizeTime = (timeValue) => {
  if (!timeValue) return ""
  if (String(timeValue).toLowerCase().includes("am") || String(timeValue).toLowerCase().includes("pm")) {
    return timeValue
  }
  return String(timeValue).slice(0, 5)
}

const BookCab = () => {
  const dispatch = useDispatch()

  const queryParams = useMemo(() => new URLSearchParams(window.location.search), [])
  const token = queryParams.get("token") || queryParams.get("visitor_unique_id")
  const vehicleId = queryParams.get("vehicle_id") || "1"
  const coupon = queryParams.get("coupon") || "PULPIT10"

  const [isRestoring, setIsRestoring] = useState(Boolean(token))
  const [restoreError, setRestoreError] = useState("")
  const [restoredTripData, setRestoredTripData] = useState(null)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    if (!token) return

    let cancelled = false

    const restoreBookingFromCarousel = async () => {
      setIsRestoring(true)
      setRestoreError("")
      setRestoredTripData(null)

      // Important: carousel links can open in any browser/device.
      // Do not show old localStorage data while restoring this token.
      localStorage.removeItem("tripData")
      localStorage.removeItem("cabSearchResult")
      localStorage.removeItem("routePath")
      localStorage.removeItem("autoCouponCode")
      dispatch(cabAction.resetCabBookingContext?.())

      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || "https://customerapi.pulpitcabs.com"
        const response = await axios.post(
          `${backendUrl}/website/visitors_trip_fare`,
          { visitor_unique_id: token },
          { headers: { "Content-Type": "application/json" } }
        )

        if (cancelled) return

        const payload = response?.data || {}
        const cabList = Array.isArray(payload?.data) ? payload.data : []
        const selectedCab =
          cabList.find((cab) => String(cab?.vehicle_id) === String(vehicleId)) ||
          cabList[0]

        if (!selectedCab) {
          throw new Error("Cab details not found for this booking link.")
        }

        const visitor = payload?.visitor_details || {}
        const pickupAddress = visitor?.pickup_address || visitor?.pickup || ""
        const dropAddress = visitor?.drop_address || visitor?.drop || ""
        const pickupCity = visitor?.pickup_city || extractCity(pickupAddress)
        const dropCity = visitor?.drop_city || extractCity(dropAddress)
        const tripDate = normalizeDate(visitor?.booked_date || visitor?.trip_date)
        const tripTime = normalizeTime(visitor?.booked_time || visitor?.trip_time)
        const phoneNumber = String(visitor?.phone_number || "").replace(/^91/, "")

        const tripData = {
          trip_type: Number(visitor?.trip_type || 3),
          round_trip: visitor?.is_round_trip ? 1 : 0,
          is_round_trip: visitor?.is_round_trip ? 1 : 0,

          pickup: pickupAddress,
          drop: dropAddress,
          pickup_city: pickupCity,
          drop_city: dropCity,

          pickup_lat: visitor?.pickup_latitude || null,
          pickup_long: visitor?.pickup_longitude || null,
          drop_lat: visitor?.drop_latitude || null,
          drop_long: visitor?.drop_longitude || null,

          package_type_in_hours: "",
          trip_date: tripDate,
          trip_time: tripTime,
          phone_number: phoneNumber,
          mobile_number: phoneNumber,
          ip_address: visitor?.ip_address || "",

          return_date:
            visitor?.return_date && visitor.return_date !== "Invalid date"
              ? normalizeDate(visitor.return_date)
              : "",
          return_time: visitor?.return_time || "",

          visitorEntryId: payload?.visitorEntryId || visitor?.id || "",
          web_trip_unique_id: payload?.web_trip_unique_id || token,
          distance: payload?.distance || visitor?.KM || "",
          cab: selectedCab,
        }

        localStorage.setItem("tripData", JSON.stringify(tripData))
        localStorage.setItem("cabSearchResult", JSON.stringify(payload))
        localStorage.setItem("autoCouponCode", coupon)
        setRestoredTripData(tripData)

        if (pickupCity && dropCity) {
          localStorage.setItem(
            "routePath",
            `${pickupCity.toLowerCase().replace(/\s+/g, "-")}-to-${dropCity.toLowerCase().replace(/\s+/g, "-")}-cab`
          )
        }

        dispatch(cabAction.setCabSearchResult(payload))

        dispatch(
          bookNow({
            visitorEntryId: payload?.visitorEntryId || visitor?.id || "",
            web_trip_unique_id: payload?.web_trip_unique_id || token,
            vehicle_id: selectedCab?.vehicle_id,
            grand_total: selectedCab?.total,
            advance_booking: selectedCab?.advance_booking || 20,
            advance_booking_amount: Number(selectedCab?.total || 0) * 0.2,
            GST: selectedCab?.gst_percentage,
            GSTCalculate: selectedCab?.gst,
            distance: payload?.distance || visitor?.KM || "",
            estimate_fare: selectedCab?.subtotal,
          })
        )
      } catch (error) {
        if (!cancelled) {
          setRestoreError(error?.response?.data?.message || error?.message || "Unable to restore booking details.")
        }
      } finally {
        if (!cancelled) setIsRestoring(false)
      }
    }

    restoreBookingFromCarousel()

    return () => {
      cancelled = true
    }
  }, [dispatch, token, vehicleId, coupon])

  if (isRestoring) {
    return (
      <div className="pt-[120px] min-h-screen flex items-center justify-center">
        <Loading />
      </div>
    )
  }

  if (restoreError) {
    return (
      <div className="pt-[120px] min-h-screen text-center px-4">
        <h2 className="text-2xl font-bold mb-3">Booking link could not be opened</h2>
        <p className="text-gray-600">{restoreError}</p>
      </div>
    )
  }

  return (
    <div>
      <BookCabMain restoredTripData={restoredTripData} />
    </div>
  )
}

export default BookCab
